import {useEffect, useRef, useState} from 'react';

import {useAccessToken, useCommerceApi, useProductSearch} from '@salesforce/commerce-sdk-react';
import {useQuery} from '@tanstack/react-query';

import {chunk, GET_PRODUCTS_PAGE_SIZE, GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';

// productSearch: page size and max pages (cache-friendly: page 1 keyed by useProductSearch params).
const SEARCH_PAGE_SIZE = 200;
const SEARCH_MAX_PAGES = 5;

const searchParams = (categoryId, offset = 0) => ({
  refine: [`cgid=${categoryId}`],
  limit: SEARCH_PAGE_SIZE,
  offset,
});

/**
 * Paginated productSearch: page 1 via useProductSearch (React Query cache), remaining pages in parallel.
 * isComplete is true only after all pages are fetched so getProducts runs once on the final ID list.
 */
const usePaginatedProductSearch = (categoryId) => {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();

  // Refs keep the latest values accessible inside effects without being deps.
  // Both api and getTokenWhenReady can have unstable references across renders
  // (new function/object identity each render), which would cause infinite loops
  // if included in useEffect deps. Refs solve this without stale-closure risk.
  const apiRef = useRef(api);
  const getTokenRef = useRef(getTokenWhenReady);

  apiRef.current = api;
  getTokenRef.current = getTokenWhenReady;

  const [extraHits, setExtraHits] = useState([]);
  const [isExtraLoading, setIsExtraLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const {data: firstPageResult, isLoading: isFirstPageLoading} = useProductSearch(
    {parameters: searchParams(categoryId, 0)},
    {enabled: !!categoryId}
  );

  const firstPageHits = firstPageResult?.hits ?? [];
  const total = firstPageResult?.total ?? 0;

  useEffect(() => {
    setIsComplete(false);

    const totalPages = Math.ceil(total / SEARCH_PAGE_SIZE);
    const cappedPages = Math.min(totalPages, SEARCH_MAX_PAGES);
    const remainingPages = cappedPages - 1;

    if (!categoryId || !firstPageResult || remainingPages <= 0) {
      setExtraHits([]);
      setIsExtraLoading(false);
      setSearchError(null);
      if (firstPageResult) setIsComplete(true);

      return;
    }

    setSearchError(null);

    let cancelled = false;

    const fetchRemainingPages = async () => {
      setIsExtraLoading(true);
      const offsets = Array.from({length: remainingPages}, (_, i) => (i + 1) * SEARCH_PAGE_SIZE);

      try {
        const token = await getTokenRef.current();
        const results = await Promise.all(
          offsets.map((offset) => apiRef.current.shopperSearch.productSearch({parameters: searchParams(categoryId, offset), headers: {Authorization: `Bearer ${token}`}}).then((res) => res?.hits ?? []))
        );

        if (!cancelled) {
          setExtraHits(results.flat());
          setIsComplete(true);
        }
      } catch (err) {
        if (!cancelled) {
          setExtraHits([]);
          setIsComplete(true);
          setSearchError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setIsExtraLoading(false);
      }
    };

    fetchRemainingPages();

    return () => {
      cancelled = true;
    };
  }, [categoryId, total]);

  return {
    hits: [...firstPageHits, ...extraHits],
    total,
    isLoading: isFirstPageLoading || isExtraLoading,
    isComplete: isComplete && !isFirstPageLoading,
    error: searchError,
  };
};

/**
 * Fetches products by ID in chunks via React Query.
 * Same product IDs reuse the cached response; no duplicate network calls across remounts.
 */
const usePaginatedProducts = (productIds, inventoryIdsParam) => {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const idsKey = productIds.join(',');
  const invKey = inventoryIdsParam ?? '';

  const {data, isLoading, isFetched, error} = useQuery({
    queryKey: ['king-living/getProducts', idsKey, invKey],
    queryFn: async () => {
      if (productIds.length === 0) return [];
      const token = await getTokenWhenReady();
      const chunks = chunk(productIds, GET_PRODUCTS_PAGE_SIZE);
      const results = await Promise.all(
        chunks.map((ids) => api.shopperProducts
          .getProducts({
            parameters: {
              ids: ids.join(','),
              ...GET_PRODUCTS_PARAMS,
              ...(inventoryIdsParam ? {inventoryIds: inventoryIdsParam} : {}),
            },
            headers: {Authorization: `Bearer ${token}`},
          })
          .then((res) => res?.data ?? []))
      );

      return results.flat();
    },
    enabled: productIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return {data: {data: data ?? []}, isLoading, isFetched, error: error ?? null};
};

/**
 * True if product is a configuration option (out-of-stock but orderable).
 * MTO: Out-of-stock orderable: ats === 0 && orderable === true.
 */
const isConfigProduct = (p) => {
  const inv = p.inventory ?? {};

  return (inv.ats === 0 && inv.orderable === true);
};

/** True if product is genuinely in-stock (ATS > 0 and orderable). */
const isInstockProduct = (p) => {
  const inv = p.inventory ?? {};

  return inv.ats > 0 && inv.orderable === true;
};

/**
 * Normalize product for selector: use c_priceProcessed (SFRA) when present, else fallback to price/prices.
 * Ensures price, prices.{salePrice, listPrice, min, max}, priceType, priceMin, priceMax for tile.
 */
const withNormalizedPrice = (p) => {
  let salePrice;
  let listPrice;
  let min;
  let max;
  let priceType = 'single';

  const raw = p.c_priceProcessed;

  if (raw) {
    try {
      const processed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (processed && typeof processed.salePrice === 'number') {
        salePrice = processed.salePrice;
        min = processed.min ?? salePrice;
        max = processed.max ?? salePrice;
        priceType = processed.type === 'range' ? 'range' : 'single';
        // listMin = min of list/retail pricebook (range products, set by SFRA hook)
        // listPrice = single product list price
        listPrice = processed.listMin ?? processed.listPrice ?? salePrice;
      }
    } catch {
      // Fall through to fallback
    }
  }

  if (salePrice == null) {
    salePrice = p.price ?? p.prices?.salePrice;
    listPrice = p.prices?.listPrice ?? salePrice;
    min = salePrice;
    max = salePrice;
  }

  // Fallback: derive listPrice from priceRanges (available via perPricebook: true in getProducts).
  // The retail/list range has a higher minPrice than the sale range — use it as strikethrough.
  if (listPrice == null || listPrice <= (salePrice ?? 0)) {
    const listRange = (p.priceRanges ?? []).find(
      (r) => typeof r.minPrice === 'number' && r.minPrice > (min ?? salePrice ?? 0)
    );
    if (listRange) listPrice = listRange.minPrice;
  }

  const prices = {...(p.prices || {}), salePrice, listPrice, min, max};

  return {
    ...p,
    price: salePrice,
    prices,
    priceType,
    priceMin: min,
    priceMax: max,
  };
};

/**
 * Configuration products for the selector: search by category (paginated), then getProducts by ID.
 * getProducts is called only when search is complete (isSearchComplete) so the ID list is final — single request set, cache-friendly.
 *
 * Returns configProducts (MTO / out-of-stock orderable), instockProducts (in-stock orderable),
 * isLoading, isFetched, total, isError, and error (last fetch error, if any).
 * Hooks are always called in the same order (Rules of Hooks); when product doesn't need config products we pass undefined/empty so no fetches run.
 *
 * Supports both Complex PDP and Simple PDP with configurable options.
 */
export const useConfigurationProducts = (product, isComplexProduct, hasConfigurableOptions = false) => {
  const shopperInventoryIds = useShopperInventoryIds();
  const shouldFetchConfigProducts = true;
  const categoryId = shouldFetchConfigProducts ? product?.primaryCategoryId : undefined;
  const {hits: searchHits, total, isLoading: isSearchLoading, isComplete: isSearchComplete, error: searchError} = usePaginatedProductSearch(categoryId);

  const productIdArray = shouldFetchConfigProducts && isSearchComplete ?
    searchHits.map((h) => h.productId || h.product_id).filter(Boolean) :
    [];

  const {data: productsResult, isLoading: isProductsLoading, isFetched, error: productsError} = usePaginatedProducts(
    productIdArray,
    shopperInventoryIds
  );
  const allProducts = productsResult?.data ?? [];

  const instockProducts = shouldFetchConfigProducts ? allProducts.filter(isInstockProduct).map((p) => withNormalizedPrice(p)) : [];

  const instockProductIds = new Set(
    instockProducts.map((p) => p.id || p.productId)
  );

  const configProducts = shouldFetchConfigProducts ?
    allProducts
      .filter(isConfigProduct)
      .filter((p) => !instockProductIds.has(p.id || p.productId))
      .map((p) => withNormalizedPrice({...p, displayTitle: p.name ?? ''})) :
    [];

  const error = searchError ?? productsError;
  const isError = Boolean(error);

  return {
    configProducts,
    instockProducts,
    isLoading: isSearchLoading || isProductsLoading,
    isFetched,
    total: total ?? 0,
    isError,
    error,
  };
};
